import { useEffect, useState } from "react";
import { format } from "date-fns";
import { getAvailableSlots, TimeSlot } from "@/lib/availabilityChecker";
import { TIMEZONE_LABEL } from "@/lib/timezoneConfig";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, RefreshCw, Clock, AlertCircle, Lock } from "lucide-react";
import { cn } from "@/lib/utils";

interface TimeSlotSelectorProps {
  selectedDate: Date | undefined;
  selectedSlot: TimeSlot | null;
  onSlotSelect: (slot: TimeSlot | null) => void;
  className?: string;
}

export default function TimeSlotSelector({
  selectedDate,
  selectedSlot,
  onSlotSelect,
  className,
}: TimeSlotSelectorProps) {
  const [slots, setSlots] = useState<TimeSlot[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSlots = async () => {
    if (!selectedDate) {
      setSlots([]);
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const availableSlots = await getAvailableSlots(selectedDate);
      setSlots(availableSlots);
      
      // Clear selection if previously selected slot is no longer available
      if (selectedSlot) {
        const stillAvailable = availableSlots.find(
          s => s.startTime.getTime() === selectedSlot.startTime.getTime() && s.isAvailable
        );
        if (!stillAvailable) {
          onSlotSelect(null);
        }
      }
    } catch (err) {
      console.error('Error fetching time slots:', err);
      setError('Failed to load available time slots. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSlots();
  }, [selectedDate]);

  // Auto-refresh slots every 30 seconds to prevent stale data
  useEffect(() => {
    if (!selectedDate) return;
    
    const interval = setInterval(() => {
      fetchSlots();
    }, 30000); // 30 seconds
    
    return () => clearInterval(interval);
  }, [selectedDate]);

  if (!selectedDate) {
    return (
      <Card className={cn("p-6 text-center border-dashed", className)}>
        <Clock className="h-12 w-12 mx-auto mb-3 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">
          Please select a date first to view available time slots
        </p>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card className={cn("p-6 text-center", className)}>
        <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2 text-primary" />
        <p className="text-sm text-muted-foreground">Loading available time slots...</p>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={cn("p-6 text-center border-destructive", className)}>
        <AlertCircle className="h-12 w-12 mx-auto mb-3 text-destructive" />
        <p className="text-sm text-destructive mb-3">{error}</p>
        <Button variant="outline" size="sm" onClick={fetchSlots} type="button">
          <RefreshCw className="h-4 w-4 mr-2" />
          Try Again
        </Button>
      </Card>
    );
  }

  if (slots.length === 0) {
    const isToday = format(selectedDate, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
    
    return (
      <Card className={cn("p-6 text-center border-dashed", className)}>
        <Clock className="h-12 w-12 mx-auto mb-3 text-muted-foreground" />
        <p className="text-sm font-semibold mb-1">
          {isToday ? "No remaining time slots today" : "No time slots available"}
        </p>
        <p className="text-xs text-muted-foreground mb-3">
          {isToday 
            ? "All time slots for today have passed or are booked. Please select a future date."
            : "This day may be fully booked or unavailable. Please select another date."
          }
        </p>
        <Button variant="outline" size="sm" onClick={fetchSlots} type="button">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </Card>
    );
  }

  const availableSlots = slots.filter(s => s.isAvailable);
  const unavailableSlots = slots.filter(s => !s.isAvailable);

  return (
    <div className={cn("space-y-4", className)}>
      <div className="flex items-center justify-between">
        <div>
          <h4 className="font-semibold">Time Slots ({TIMEZONE_LABEL})</h4>
          <p className="text-xs text-muted-foreground">
            {format(selectedDate, 'EEEE, MMMM d, yyyy')} • {availableSlots.length} available, {unavailableSlots.length} booked
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={fetchSlots} type="button">
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      {/* Selected Slot Display */}
      {selectedSlot && (
        <Card className="p-3 bg-primary/5 border-primary">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold">Selected Time ({TIMEZONE_LABEL})</p>
              <p className="text-lg font-bold text-primary">
                {format(selectedSlot.startTime, 'h:mm a')} - {format(selectedSlot.endTime, 'h:mm a')}
              </p>
              {selectedSlot.requiresApproval && (
                <Badge variant="outline" className="mt-1 bg-orange-500/10 text-orange-700 border-orange-300">
                  Requires Approval
                </Badge>
              )}
            </div>
            <Button variant="ghost" size="sm" onClick={() => onSlotSelect(null)} type="button">
              Change
            </Button>
          </div>
        </Card>
      )}

      {/* All Slots Grid */}
      {slots.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {slots.map((slot) => {
            const isSelected = selectedSlot?.startTime.getTime() === slot.startTime.getTime();
            
            return (
              <Button
                key={slot.startTime.toISOString()}
                variant={isSelected ? "default" : "outline"}
                disabled={!slot.isAvailable}
                className={cn(
                  "flex flex-col h-auto py-3 px-2",
                  !slot.isAvailable && "opacity-50 cursor-not-allowed bg-muted border-muted-foreground/20",
                  slot.isAvailable && slot.isAfterHours && !isSelected && "border-orange-300 bg-orange-50 hover:bg-orange-100 text-orange-900",
                  slot.isAvailable && !slot.isAfterHours && !isSelected && "border-green-300 bg-green-50 hover:bg-green-100 text-green-900"
                )}
                onClick={() => slot.isAvailable && onSlotSelect(slot)}
                type="button"
              >
                {!slot.isAvailable ? (
                  <Lock className="h-4 w-4 mb-1" />
                ) : (
                  <Clock className="h-4 w-4 mb-1" />
                )}
                <span className="font-semibold text-xs">
                  {format(slot.startTime, 'h:mm a')}
                </span>
                <span className="text-[10px] opacity-70">
                  {Math.round((slot.endTime.getTime() - slot.startTime.getTime()) / 60000)} min
                </span>
                {!slot.isAvailable ? (
                  <Badge variant="secondary" className="text-[9px] mt-1 px-1 py-0 bg-muted">
                    Booked
                  </Badge>
                ) : slot.isAfterHours ? (
                  <Badge variant="secondary" className="text-[9px] mt-1 px-1 py-0">
                    After Hours
                  </Badge>
                ) : null}
              </Button>
            );
          })}
        </div>
      )}

      {/* Legend */}
      <Card className="p-3 bg-muted/30">
        <p className="text-xs font-semibold mb-2">Legend</p>
        <div className="space-y-1 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded border-2 border-green-300 bg-green-50"></div>
            <span>Regular hours - Confirmed immediately</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded border-2 border-orange-300 bg-orange-50"></div>
            <span>After hours - Requires admin approval</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded border-2 border-gray-300 bg-gray-100"></div>
            <span>Unavailable - Already booked or blocked</span>
          </div>
        </div>
      </Card>
    </div>
  );
}
